import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Feature } from '../../classes/auth/feature.class';
import { Functionality } from '../../classes/auth/functionality';
import { Role } from '../../classes/auth/role.class';
import { User } from '../../classes/user.class';
import { LocalStorage } from '../../storage.class';
import { ApiService } from '../api/api.service';
import { ConditionalFunctions } from './conditional-functions';
import { Features } from './features';
import { Functionalities } from './functionalities';

@Injectable({
    providedIn: 'root',
})
export class AuthorisationService {
    static get roleFunctionalityMap(): Map<string, Functionalities[]> {
        return AuthorisationService._roleFunctionalityMap;
    }

    static get functionalityFeaturesMap(): Map<Functionalities, Features[]> {
        return AuthorisationService._functionalityFeaturesMap;
    }

    private static _roleFunctionalityMap: Map<string, Functionalities[]>;
    private static _functionalityFeaturesMap: Map<Functionalities, Features[]>;

    constructor(public apiService: ApiService) {}

    rightsMap() {
        return this.apiService
            .getCallData$<{
                rolesHasFunctionalities: { functionality_key: Functionalities; role_key: string }[];
                functionalitiesHasFeatures: { functionality_key: Functionalities; feature_key: Features }[];
            }>('auth/rights-map')
            .pipe(
                shareReplay(1),
                map(
                    (rights: {
                        rolesHasFunctionalities: { functionality_key: Functionalities; role_key: string }[];
                        functionalitiesHasFeatures: { functionality_key: Functionalities; feature_key: Features }[];
                    }) => {
                        const roleFunctionalityMap = new Map<string, Functionalities[]>();
                        rights.rolesHasFunctionalities.forEach(roleFunctionality =>
                            roleFunctionalityMap.set(
                                roleFunctionality.role_key,
                                rights.rolesHasFunctionalities
                                    .filter(rf => rf.role_key === roleFunctionality.role_key)
                                    .map(rf => rf.functionality_key)
                            )
                        );
                        const functionalityFeaturesMap = new Map<Functionalities, Features[]>();
                        rights.functionalitiesHasFeatures.forEach(functionalityFeature =>
                            functionalityFeaturesMap.set(
                                functionalityFeature.functionality_key,
                                rights.functionalitiesHasFeatures
                                    .filter(ff => ff.functionality_key === functionalityFeature.functionality_key)
                                    .map(ff => ff.feature_key)
                            )
                        );
                        AuthorisationService._roleFunctionalityMap = roleFunctionalityMap;
                        AuthorisationService._functionalityFeaturesMap = functionalityFeaturesMap;

                        return { roleFunctionalityMap, functionalityFeaturesMap };
                    }
                )
            );
    }

    public static hasRole(roleKey: string, otherUser?: User, params?: any): boolean {
        const user = otherUser ?? LocalStorage.user;
        return !!user?.roles?.find(
            role =>
                role.key === roleKey &&
                (!role.conditional_function || ConditionalFunctions[role.conditional_function](user, role, params))
        );
    }

    public static hasAnyRole(roleKeys: string[], otherUser?: User): boolean {
        return !!roleKeys.find(roleKey => {
            return this.hasRole(roleKey, otherUser);
        });
    }

    public static hasFunctionality(functionalityKey: Functionalities, otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.getUser();
        return !!user?.roles?.find(role => {
            return this.roleFunctionalityMap.get(role.key)?.find(functionality => functionality === functionalityKey);
        });
    }

    public static hasAnyFunctionality(
        keys: Functionalities | Functionalities[],
        user: User = LocalStorage.user
    ): boolean {
        const keyList = [keys].flat();
        return keyList.some(key => this.hasFunctionality(key, user));
    }

    public static canApproveHours(user: User = LocalStorage.user): boolean {
        return this.hasAnyFunctionality(
            [Functionalities.CommonHourCheck, Functionalities.HourCheck, Functionalities.HourCheckRestricted],
            user
        );
    }

    public static hasFeature(featureKey: Features, otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.user;
        return !!user?.roles?.find(role => {
            return this.roleFunctionalityMap
                .get(role.key)
                ?.find(functionality =>
                    this.functionalityFeaturesMap.get(functionality)?.find(feature => feature === featureKey)
                );
        });
    }

    public static hasAnyFeature(featureKeys: Features[], otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.user;
        return !!featureKeys.find(featureKey => {
            return this.hasFeature(featureKey, user);
        });
    }

    /**
     * Check if current user can check hours for a specific user (either direct or role-based)
     */
    public static canCheckUserHours(userToCheck: User, currentUser: User = LocalStorage.user): boolean {
        if (!userToCheck || !currentUser) {
            return false;
        }

        // Check direct assignment
        if (userToCheck.hours_checked_by === currentUser.id) {
            return true;
        }

        // Check role-based assignment
        if (userToCheck.hours_checked_by_role) {
            return currentUser.roles?.some(role => role.key === userToCheck.hours_checked_by_role) || false;
        }

        return false;
    }

    public getRolesMap(): Observable<Map<string, Role>> {
        return this.apiService.getCallData$<Role[]>('auth/roles').pipe(
            shareReplay(1),
            map(roles => {
                const roleMap = new Map<string, Role>();
                roles.forEach(role => roleMap.set(role.key, role));
                return roleMap;
            })
        );
    }

    public getRoles(): Observable<Role[]> {
        return this.apiService.getCallData$<Role[]>('auth/roles');
    }

    public getFunctionalities(): Observable<Functionality[]> {
        return this.apiService.getCallData$<Functionality[]>('auth/functionalities');
    }

    public getFeatures(): Observable<Feature[]> {
        return this.apiService.getCallData$<Feature[]>('auth/features');
    }

    public addFeatureToFunctionality(functionalityKey, featureKey) {
        return this.apiService.postCall$('auth/addFeatureToFunctionality', {
            functionalityKey,
            featureKey,
        });
    }

    public removeFeatureFromFunctionality(functionalityKey, featureKey) {
        return this.apiService.postCall$('auth/removeFeatureFromFunctionality', {
            functionalityKey,
            featureKey,
        });
    }

    public addFunctionalityToRole(roleKey, functionalityKey) {
        return this.apiService.postCall$('auth/addFunctionalityToRole', {
            roleKey,
            functionalityKey,
        });
    }

    public removeFunctionalityFromRole(roleKey, functionalityKey) {
        return this.apiService.postCall$('auth/removeFunctionalityFromRole', {
            roleKey,
            functionalityKey,
        });
    }
}
